/**
 * Business Card studio — edit fields live, preview at 50% scale, export a
 * 1050×600 PNG (300 DPI print-ready) via html-to-image.
 *
 * v2 additions:
 *   1. Optional pet photo (pick from saved pets OR upload) — same size as
 *      the site logo, sits beside Princess on the front.
 *   2. Optional X and TikTok handles.
 *   3. QR defaults to the member's public profile page (/profile/{username})
 *      when logged in, so scanning the card lands on their personal page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Download, RotateCcw, Info, Loader2, X, ImagePlus } from 'lucide-react';
import { BusinessCardFront } from '../components/business-card/BusinessCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_PET_PHOTO_MB = 5;

const PUBLIC_HOST = 'https://americandogclub.com';

const buildDefaults = (user) => {
  const username = user?.username || '';
  const displayName = user?.display_name || user?.full_name || 'Your Name';
  const email = user?.email || 'you@americandogclub.com';
  return {
    fullName: displayName,
    title: 'Founder',
    email,
    phone: '',
    website: 'americandogclub.com',
    tagline: 'Member-governed community for dog people',
    orgName: 'American Dog Club',
    petPhotoSrc: '',
    petName: '',
    xHandle: '',
    tiktokHandle: '',
    // qrTarget empty → studio falls back to profile URL when logged in,
    // else defaults to the website field.
    qrTarget: username ? `${PUBLIC_HOST}/profile/${username}` : '',
  };
};

export default function BusinessCard() {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const petFileRef = useRef(null);
  const [form, setForm] = useState(() => buildDefaults(user));
  const [exporting, setExporting] = useState(false);
  const [uploadingPet, setUploadingPet] = useState(false);
  const [myPets, setMyPets] = useState([]);

  // Rebuild defaults when the user identity resolves (async auth load)
  useEffect(() => {
    setForm((prev) => ({ ...buildDefaults(user), ...clearBlanks(prev) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.username]);

  // Load member's pets with photos for the "Use a saved pet" picker
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/pets/me`);
        if (cancelled) return;
        setMyPets((res.data || []).filter((p) => p.image_url));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const reset = () => setForm(buildDefaults(user));

  const handlePickSavedPet = (petId) => {
    if (petId === '__none__') {
      setForm((p) => ({ ...p, petPhotoSrc: '', petName: '' }));
      return;
    }
    const pet = myPets.find((p) => p.id === petId);
    if (pet?.image_url) {
      setForm((p) => ({ ...p, petPhotoSrc: pet.image_url, petName: pet.name || '' }));
    }
  };

  const handlePetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PET_PHOTO_MB * 1024 * 1024) {
      toast.error(`Photo too large. Max ${MAX_PET_PHOTO_MB}MB.`);
      if (petFileRef.current) petFileRef.current.value = '';
      return;
    }
    setUploadingPet(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/uploads/pet-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${BACKEND}${res.data.url}`;
      setForm((p) => ({ ...p, petPhotoSrc: url }));
      toast.success('Pet photo attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPet(false);
      if (petFileRef.current) petFileRef.current.value = '';
    }
  };

  const clearPet = () => setForm((p) => ({ ...p, petPhotoSrc: '', petName: '' }));

  // Compute the effective QR target for the preview + export
  const effectiveQrTarget = useMemo(() => {
    if (form.qrTarget && form.qrTarget.trim()) return form.qrTarget.trim();
    if (user?.username) return `${PUBLIC_HOST}/profile/${user.username}`;
    return undefined; // BusinessCardFront falls back to website
  }, [form.qrTarget, user?.username]);

  const download = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const el = cardRef.current;
      const prevTransform = el.style.transform;
      const prevOrigin = el.style.transformOrigin;
      el.style.transform = 'none';
      el.style.transformOrigin = 'top left';

      const dataUrl = await toPng(el, {
        pixelRatio: 1,
        cacheBust: true,
        skipFonts: false,
        backgroundColor: '#ffffff',
      });

      el.style.transform = prevTransform;
      el.style.transformOrigin = prevOrigin;

      const link = document.createElement('a');
      const safeName = form.fullName.replace(/\s+/g, '-').toLowerCase() || 'business-card';
      link.download = `${safeName}-business-card.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Card downloaded');
    } catch (err) {
      toast.error('Export failed — try again');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="business-card-studio">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-medium">Business Card Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preview scaled to 50%. Download is print-ready at 1050×600px (3.5"×2" @ 300 DPI).
        </p>
      </div>

      <div className="grid md:grid-cols-[380px,1fr] gap-6 items-start">
        {/* Edit panel */}
        <Card data-testid="business-card-form">
          <CardContent className="p-5 space-y-4">
            <div className="text-sm font-semibold">Card details</div>

            <div>
              <Label htmlFor="fullName">Full name</Label>
              <Input id="fullName" value={form.fullName} onChange={update('fullName')} data-testid="bc-input-name" />
            </div>
            <div>
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={form.title} onChange={update('title')} data-testid="bc-input-title" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={form.email} onChange={update('email')} data-testid="bc-input-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={update('phone')}
                placeholder="+1 (555) 123-4567"
                data-testid="bc-input-phone"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={form.website} onChange={update('website')} data-testid="bc-input-website" />
            </div>
            <div>
              <Label htmlFor="qrTarget">QR target URL</Label>
              <Input
                id="qrTarget"
                value={form.qrTarget}
                onChange={update('qrTarget')}
                placeholder={user?.username ? `${PUBLIC_HOST}/profile/${user.username}` : PUBLIC_HOST}
                data-testid="bc-input-qr"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                {user?.username
                  ? `Defaults to your profile page: /profile/${user.username}`
                  : 'Defaults to the website URL when empty.'}
              </p>
            </div>
            <div>
              <Label htmlFor="tagline">Tagline</Label>
              <Input id="tagline" value={form.tagline} onChange={update('tagline')} data-testid="bc-input-tagline" />
            </div>
            <div>
              <Label htmlFor="orgName">Organisation name</Label>
              <Input
                id="orgName"
                value={form.orgName}
                onChange={update('orgName')}
                data-testid="bc-input-org"
              />
            </div>

            {/* Pet photo */}
            <div className="border-t pt-3">
              <Label>Your dog&apos;s photo (optional)</Label>
              {form.petPhotoSrc ? (
                <div className="flex items-center gap-2 mt-2" data-testid="bc-pet-preview">
                  <img
                    src={form.petPhotoSrc}
                    alt="Selected"
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-amber-300"
                  />
                  <div className="flex-1 text-xs text-muted-foreground truncate">
                    {form.petName || 'Attached'}
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearPet} data-testid="bc-pet-clear">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 mt-2">
                  {myPets.length > 0 && (
                    <Select onValueChange={handlePickSavedPet}>
                      <SelectTrigger data-testid="bc-pet-select">
                        <SelectValue placeholder="Use a saved pet…" />
                      </SelectTrigger>
                      <SelectContent>
                        {myPets.map((pet) => (
                          <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={petFileRef}
                    onChange={handlePetUpload}
                    className="hidden"
                    id="bc-pet-upload"
                  />
                  <label
                    htmlFor="bc-pet-upload"
                    className="inline-flex items-center justify-center gap-2 px-3 py-1.5 border rounded-md text-sm cursor-pointer hover:bg-muted transition-colors"
                    data-testid="bc-pet-upload"
                  >
                    {uploadingPet ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ImagePlus className="w-4 h-4" />
                    )}
                    Upload photo
                  </label>
                </div>
              )}
            </div>

            {/* Socials */}
            <div className="border-t pt-3 space-y-3">
              <div className="text-sm font-medium">Social handles (optional)</div>
              <div>
                <Label htmlFor="xHandle" className="text-xs">X (Twitter)</Label>
                <Input
                  id="xHandle"
                  value={form.xHandle}
                  onChange={update('xHandle')}
                  placeholder="garybickford"
                  data-testid="bc-input-x"
                />
              </div>
              <div>
                <Label htmlFor="tiktokHandle" className="text-xs">TikTok</Label>
                <Input
                  id="tiktokHandle"
                  value={form.tiktokHandle}
                  onChange={update('tiktokHandle')}
                  placeholder="americandogclub"
                  data-testid="bc-input-tiktok"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={download} disabled={exporting} className="flex-1" data-testid="bc-download-btn">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting…' : 'Download PNG'}
              </Button>
              <Button variant="outline" size="icon" onClick={reset} aria-label="Reset" data-testid="bc-reset-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                For pro print: send the PNG to Vistaprint or MOO. Add 1/8" bleed setting when uploading —
                the amber accent bar bleeds off the left edge intentionally.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Preview (50%)</div>
              <div
                className="bg-gray-100 rounded-lg overflow-hidden border"
                style={{ width: '525px', height: '300px' }}
                data-testid="business-card-preview"
              >
                <BusinessCardFront
                  ref={cardRef}
                  fullName={form.fullName}
                  title={form.title}
                  email={form.email}
                  phone={form.phone}
                  website={form.website}
                  tagline={form.tagline}
                  orgName={form.orgName}
                  petPhotoSrc={form.petPhotoSrc}
                  petName={form.petName}
                  xHandle={form.xHandle}
                  tiktokHandle={form.tiktokHandle}
                  qrTarget={effectiveQrTarget}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-amber-50/60 border-amber-200">
            <CardContent className="p-4 text-xs text-amber-900 space-y-1.5">
              <div className="font-semibold">Reuse notes</div>
              <p>
                Same <code>BusinessCardFront</code> component will power NGO / partner promo cards
                later — swap <code>logoSrc</code>, <code>orgName</code>, and pass an <code>accent</code>
                prop to switch colour families (currently locked to amber for the Club).
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Keep any user-typed overrides when the async auth load rebuilds defaults
function clearBlanks(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  );
}
